import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import { GatewayLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/locations/models/outputs/gateway-location.model';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useParams } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { LocationUpdateController } from '@app/feature/location/location-update/location-update.controller';
import { useCpmsBoGetLocationQuery } from '@service/cpms-bo-location.service';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';
import { useSetPageBreadcrumbContext } from '@shared/layout/page-layout/page-layout-breadcrumb.context-provider';
import { useSetPageTitleContext } from '@shared/layout/page-layout/page-layout-title.context-provider';

import { LocationContextProvider } from '../location.context';
import { LocationDetailsView } from './location-details.view';

interface LocationDetailsControllerProps {
  resource: GatewayLocationModel;
}

const LocationDetailsController = (
  props: LocationDetailsControllerProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { resource } = props;
  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionUpdateIsDialogOpen, setActionUpdateIsDialogOpen] =
    useState(false);

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(resource.name);
  }, [resource, setPageTitle]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.LOCATIONS,
        action: ActionsByResource.LOCATIONS.UPDATE,
      })
    ) {
      actions.push(
        <ButtonIcon
          onClick={() => setActionUpdateIsDialogOpen(true)}
          icon="edit-05"
          key="update"
        />,
      );
    }

    setPageAction(actions);
  }, [setPageAction, userPermissions]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  const setPageBreadcrumb = useSetPageBreadcrumbContext();

  useEffect(() => {
    if (!resource?.network?.id || !resource?.network?.name) {
      return;
    }

    setPageBreadcrumb([
      {
        label: resource.network.name,
        url: generatePath(APP_PATH.NETWORK.DETAILS, {
          id: resource.network.id,
        }),
        iconName: 'server-06',
      },
    ]);
  }, [setPageBreadcrumb, resource, t]);

  // HANDLERS //////////////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <LocationContextProvider data={resource}>
      <LocationDetailsView />
      <Dialog
        onClose={() => setActionUpdateIsDialogOpen(false)}
        open={actionUpdateIsDialogOpen}
        confirmClose
        height="100%"
        size={DialogSize.LARGE}>
        <Dialog.Header
          title={t('$.location-update.title')}
          icon="map-01"
          onClose={() => setActionUpdateIsDialogOpen(false)}
        />
        <LocationUpdateController
          onCancel={() => setActionUpdateIsDialogOpen(false)}
          onSuccess={() => setActionUpdateIsDialogOpen(false)}
        />
      </Dialog>
    </LocationContextProvider>
  );
};

export const LocationDetailsPage = (): React.JSX.Element => {
  const userPermissions = useAppSelector(permissions);
  const params = useParams();
  const queryState = useCpmsBoGetLocationQuery(params.id ?? '');

  const { Component } = withProtectedResource<
    GatewayLocationModel,
    {},
    GatewayLocationModel
  >(LocationDetailsController, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.LOCATIONS,
        action: ActionsByResource.LOCATIONS.READ,
      }),
  })({});

  return Component;
};
